<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount, defineAsyncComponent, createVNode, render, DefineComponent } from 'vue'
import { api } from '../../../api/Api'
import MessageBox from '../../../services/MessageBox'
import { tokenManager } from '../../../api/utils/tokenManager';
import { Permission } from '../../../types/auth';
import IconComponent from '../common/IconComponent.vue';

const props = defineProps<{
  modelValue: string[] | '' | null | undefined
  options?: { id: string; name: string }[]
  label: string
  placeholder?: string
  error?: string | null
  endPoint?: string
  readonly?: boolean
  allowNewRecord?: boolean
  remote?: boolean,
  disabled?: boolean
  localStorage?: string
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | string[]): void
  (e: 'blur'): void
}>()

const isOpen = ref(false)
const searchQuery = ref('')
const selectedItems = ref<{ id: string; name: string }[]>([])
const isLoading = ref(false)
const isHovered = ref(false)
const items = ref<{ id: string; name: string }[]>([])
const inputRef = ref<HTMLInputElement | null>(null)
const dropdownRef = ref<HTMLDivElement | null>(null)

const filteredOptions = computed(() => {
  if (!searchQuery.value) return items.value
  
  const query = searchQuery.value.toLowerCase()
  return items.value.filter(option => 
    option.name.toLowerCase().includes(query)
  )
})

const showAddNew = computed(() => {
  return (props.allowNewRecord && 
         searchQuery.value && 
         filteredOptions.value.length === 0 && 
         !props.readonly) || 
         (props.allowNewRecord && items.value.length <= 0)
})

const isItemSelected = (itemId: string) => {
  return Array.isArray(props.modelValue) && props.modelValue.includes(itemId)
}

const handleSelect = (option: { id: string; name: string }, event: Event) => {
  event.preventDefault()
  event.stopPropagation()
  
  // Don't allow selecting already selected items
  if (isItemSelected(option.id)) {
    return
  }
  
  const newValue = Array.isArray(props.modelValue) ? 
      [...props.modelValue, option.id] : 
      [option.id]
    emit('update:modelValue', newValue)
    selectedItems.value = [...selectedItems.value, option]
  
  searchQuery.value = ''
 
}

const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement
  if (!dropdownRef.value?.contains(target) && !inputRef.value?.contains(target)) {
    isOpen.value = false
    emit('blur')
    searchQuery.value = ''
  }
}

const removeItem = (item: { id: string; name: string }, event: Event) => {
  event.stopPropagation()
  const newValue = (props.modelValue as string[]).filter(id => id !== item.id)
    emit('update:modelValue', newValue)
    selectedItems.value = selectedItems.value.filter(i => i.id !== item.id)
}

const loadOptions = async () => {
  if (!props.endPoint || props.remote) {
    items.value = props.options || []
    updateSelectedItems()
    return
  }
  const localData = props.label ? tokenManager.getList((props.localStorage ? props.localStorage : props.label).replace(' ', '_').toLowerCase()) : null;
  if (localData && localData.length > 0) {  
    items.value = localData || []
    if (items.value.length > 0) {
      updateSelectedItems()
      return
    }
  }

  loadFromApi()
  
}


const loadFromApi = async () => {
  try {
    isLoading.value = true
    const response = await api.post(props.endPoint + '/list', null,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        skipLoading: true,
      })
    if (response.data) {
      items.value = response.data.map((item: any) => ({
        id: item.id.toString(),
        name: item.name
      }))
      updateSelectedItems()
    }
  } catch (error: any) {
    console.error('Error loading options:', error)
    MessageBox.show(error.message || 'Failed to load options', 'error')
  } finally {
    isLoading.value = false
  }
}
const updateSelectedItems = () => {
  if (props.modelValue) {
    const values = Array.isArray(props.modelValue) ? props.modelValue : [props.modelValue]
    selectedItems.value = values
      .map(value => items.value.find(item => item.id === value))
      .filter((item): item is { id: string; name: string } => item !== undefined)
  } else {
    selectedItems.value = []
  }
}

const showDropDown = () => {
  if (!props.disabled && !props.readonly) 
    isOpen.value = true
}


const handleAddNew = async () => {
  if (!props.allowNewRecord) return
  try {
    // Dynamically import the component using the fixed path
    const comp_path = props.endPoint?.replace("/admin", "").replace(/-/g, "");
const comp =
  (comp_path?.slice(-1) === "/" ? "" : comp_path + "/") +
  props.label?.replace(/ /g, "") +
  (props.label?.slice(-1) === "s" ? "" : "s") +
  "Form.vue";
  const modalComponent = `/src/features/${comp}`;
  //const modalComponent = '/src/features/revenue/masterdata/customertypes/CustomerTypesForm.vue'


// Preload all modal components
const modalModules = import.meta.glob('@features/**/*Form.vue');
const FormModal = defineAsyncComponent(() => {
      const module = modalModules[modalComponent];
      if (!module) {
        MessageBox.show("Could not load modal component", "error");
        return import('../../../features/FallbackForm.vue') as unknown as Promise<DefineComponent>;
      }
      return module() as Promise<DefineComponent>;
    });

    // Create modal container
    const container = document.createElement("div");
    document.body.appendChild(container);

    const perm: Permission[] = tokenManager.getPermissions();
    const rpath = props.endPoint?.replace("clients/", "");
    const permissions = perm.filter(function (itm) {
      return itm.route_path == rpath;
    });
    // Create and mount modal
    const vnode = createVNode(FormModal, {
      permissions: permissions,
      title: props.label,
      endPoint: props.endPoint,
      mode: "create",
      listCreateName: searchQuery.value,
      onSave: async (newRecord: any) => {
        const newOption = {
          id: newRecord.id.toString(),
          name: newRecord.name,
        };
        items.value = [...items.value, newOption];

        // Select the new record
        //handleSelect(newOption); //TODO: check 

        // Remove modal
        render(null, container);
        container.remove();
      },
      onClose: () => {
        render(null, container);
        container.remove();
      },
    });

    // Render the modal
    render(vnode, container);
  } catch (error) {
    console.error("Error loading modal component:", error);
  }
};

// Initialize
onMounted(() => {
  loadOptions()
  document.addEventListener('click', handleClickOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
})

// Watch for modelValue changes
watch(() => props.modelValue, () => {
  updateSelectedItems()
}, { immediate: true })

// Watch for options prop changes
watch(() => props.options, () => {
  loadOptions()
}, { deep: true })
</script>

<template>
  <div class="relative">
    <label v-if="label" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
      {{ label }}
    </label>
    
    <div 
      class="relative"
      @mouseenter="isHovered = true"
      @mouseleave="isHovered = false"
    >
      <!-- Selected Items Display -->
      <div
        ref="inputRef"
        class="w-full min-h-[38px] px-3 py-2 rounded-lg border text-gray-900 dark:text-white bg-white dark:bg-gray-900 cursor-text flex flex-wrap gap-2 items-center"
        :class="[
          error
            ? 'border-red-300 dark:border-red-700'
            : 'border-gray-200 dark:border-gray-700',
          readonly
            ? 'bg-gray-50 dark:bg-gray-800 cursor-not-allowed'
            : ''
        ]"
        @click="showDropDown"
      >
        <!-- Selected Items Tags -->
        <div
          v-for="item in selectedItems"
          :key="item.id"
          class="bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200 px-2 py-1 rounded-md text-sm flex items-center gap-1"
        >
          <span>{{ item.name }}</span>
          <button
            v-if="!readonly"
            @click="(e) => removeItem(item, e)"
            class="hover:text-blue-600 dark:hover:text-blue-400"
          >
            <IconComponent name="Trash2" class="w-4 h-4" />
          </button>
        </div>

        <!-- Search Input -->
        <input
          v-model="searchQuery"
          type="text"
          v-if="!readonly"
          :disabled="disabled"
          :placeholder="selectedItems.length === 0 ? placeholder : ''"
          :readonly="readonly"
          class="flex-1 min-w-[50px] bg-transparent border-none outline-none p-0"
        />
      </div>
    </div>

    <!-- Error message -->
    <p v-if="error" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ error }}</p>

    <!-- Dropdown -->
    <div
      v-if="isOpen && !readonly"
      ref="dropdownRef"
      class="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 max-h-60 overflow-y-auto"
    >
      <!-- Loading state -->
      <div v-if="isLoading" class="p-4 text-center text-gray-500 dark:text-gray-400">
        Loading...
      </div>

      <!-- No results -->
      <div v-else-if="filteredOptions.length === 0 && !showAddNew" class="p-4 text-center text-gray-500 dark:text-gray-400">
        No results found
      </div>

      <!-- Options list -->
      <template v-else>
        <button
          v-for="option in filteredOptions"
          :key="option.id"
          @click="(event) => handleSelect(option, event)"
          @mousedown.prevent
          class="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center justify-between"
          :class="[
            isItemSelected(option.id)
              ? 'bg-blue-50 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400'
              : 'text-gray-900 dark:text-white'
          ]"
          :disabled="isItemSelected(option.id)"
        >
          <span>{{ option.name }}</span>
          <span v-if="isItemSelected(option.id)" class="text-blue-600 dark:text-blue-400">✓</span>
        </button>

        <!-- Add New Option -->
        <button
          v-if="showAddNew"
          @click.stop="handleAddNew"
          @mousedown.prevent
          class="w-full px-4 py-2 text-left text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/50 flex items-center gap-2"
        >
          <IconComponent name="Plus"/>
          Add {{ searchQuery.length > 0 ? '"' + searchQuery + '"' : 'new ' + props.label }}
        </button>
      </template>
    </div>
  </div>
</template>